import React, { useState, useEffect, useRef } from 'react';
import DashboardService from '../services/dashboardService';
import DashboardControls from '../components/DashboardControls';
import './Dashboard.css';
import '../components/css/ClipsView.css';

// Add a simple toast function at the top level of the file
const showToast = (message) => {
  // Create toast element
  const toast = document.createElement('div');
  toast.className = 'copy-toast';
  toast.textContent = message;
  
  // Add to body
  document.body.appendChild(toast);
  
  // Show the toast
  setTimeout(() => {
    toast.classList.add('visible');
  }, 10);
  
  // Hide and remove after 2 seconds
  setTimeout(() => {
    toast.classList.remove('visible');
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 300);
  }, 2000);
};

// Helper component for video with fallback - Updated for reel format
const VideoPlayer = ({ src, fallbackSrc, onLoad, isClip = false, isReel = false }) => {
  const videoRef = useRef(null);
  const [isVertical, setIsVertical] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setCurrentSrc(src);
    
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [src]);

  // Register event listeners for play events to manage video playback
  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (!videoElement) return;
    
    // Handler to pause other videos when this one plays
    const handlePlay = () => {
      // Find all videos in the document
      const allVideos = document.querySelectorAll('video');
      
      // Pause all videos except the one that's playing
      allVideos.forEach(video => {
        if (video !== videoElement && !video.paused) {
          video.pause();
        }
      });
    };
    
    // Add event listener
    videoElement.addEventListener('play', handlePlay);
    
    // Clean up
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('play', handlePlay);
      }
    };
  }, []);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const { videoWidth, videoHeight } = videoRef.current;
      // Set vertical flag if height > width
      const isVerticalVideo = videoHeight > videoWidth;
      setIsVertical(isVerticalVideo);
      setDimensions({ width: videoWidth, height: videoHeight });
      
      // Apply styling to ensure video is centered and properly sized
      const videoElement = videoRef.current;
      
      if (isReel) {
        // Reel format - preserve aspect ratio, center video, add black padding if needed
        videoElement.style.maxWidth = '100%';
        videoElement.style.maxHeight = '100%';
        videoElement.style.width = 'auto';
        videoElement.style.height = 'auto';
        videoElement.style.objectFit = 'contain';
        videoElement.style.backgroundColor = '#000';
        videoElement.style.display = 'block';
        videoElement.style.margin = '0 auto';
        
        // Add proper positioning for letterboxing
        videoElement.style.position = 'absolute';
        videoElement.style.top = '50%';
        videoElement.style.left = '50%';
        videoElement.style.transform = 'translate(-50%, -50%)';
        
        setIsLoading(false);
        if (onLoad) onLoad();
        return;
      }
      
      // Basic styling for all videos
      videoElement.style.maxWidth = '100%';
      videoElement.style.maxHeight = '100%';
      videoElement.style.margin = '0 auto';
      videoElement.style.display = 'block';
      
      // Special handling for clip thumbnails
      if (isClip) {
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover'; // Fill the space completely
        return setIsLoading(false);
      }
      
      // Apply specific styling based on aspect ratio for non-clip videos
      if (isVerticalVideo) {
        // Vertical video (portrait)
        videoElement.style.width = 'auto';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'contain';
      } else {
        // Horizontal video (landscape)
        if (videoWidth / videoHeight > 16/9) {
          // Extra wide video
          videoElement.style.width = '100%';
          videoElement.style.height = 'auto';
        } else {
          // Regular landscape video
          videoElement.style.width = 'auto';
          videoElement.style.height = '100%';
          videoElement.style.maxWidth = '100%';
        }
      }
      
      setIsLoading(false);
      if (onLoad) onLoad();
    }
  };

  const handleError = () => {
    console.error("Video error loading:", currentSrc);
    
    // Try fallback URL if available and we haven't tried it yet
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      console.log("Trying fallback URL:", fallbackSrc);
      setCurrentSrc(fallbackSrc);
      setIsLoading(true);
      setHasError(false);
      
      if (videoRef.current) {
        videoRef.current.load();
      }
    } else {
      setIsLoading(false);
      setHasError(true);
    }
  };

  return (
    <>
      {isLoading && (
        <div className="video-loading">
          <Loader size="small" />
        </div>
      )}
      {hasError ? (
        <div className="video-error">
          <p>Failed to load video</p>
          <a href={currentSrc} target="_blank" rel="noopener noreferrer">Open video in new tab</a>
          {fallbackSrc && currentSrc !== fallbackSrc && (
            <a href={fallbackSrc} target="_blank" rel="noopener noreferrer">Try fallback URL</a>
          )}
        </div>
      ) : (
        <video
          ref={videoRef}
          src={currentSrc}
          controls
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
          className={`${isVertical ? 'vertical-video' : ''} ${isReel ? 'reel-video' : ''}`}
        />
      )}
    </>
  );
};

// Loader component
const Loader = () => (
  <div className="loader-container">
    <div className="loader"></div>
  </div>
);

// Feedback component for clips
const ClipFeedback = ({ clipId, initialFeedback = null, onFeedbackSubmit }) => {
  const [feedback, setFeedback] = useState(initialFeedback || { liked: false, disliked: false, remarks: '' });
  const [showRemarks, setShowRemarks] = useState(false);
  
  // Effect to update when initialFeedback changes
  useEffect(() => {
    if (initialFeedback) {
      setFeedback(initialFeedback);
    }
  }, [initialFeedback, clipId]);
  
  const handleLike = () => {
    if (feedback.disliked) {
      // If currently disliked, switch to liked
      const newFeedback = { liked: true, disliked: false, remarks: feedback.remarks };
      setFeedback(newFeedback);
      onFeedbackSubmit(clipId, newFeedback);
    } else {
      // Toggle liked status
      const newFeedback = { ...feedback, liked: !feedback.liked };
      setFeedback(newFeedback);
      onFeedbackSubmit(clipId, newFeedback);
    }
  };
  
  const handleDislike = () => {
    if (feedback.liked) {
      // If currently liked, switch to disliked
      const newFeedback = { liked: false, disliked: true, remarks: feedback.remarks };
      setFeedback(newFeedback);
      onFeedbackSubmit(clipId, newFeedback);
    } else {
      // Toggle disliked status
      const newFeedback = { ...feedback, disliked: !feedback.disliked };
      setFeedback(newFeedback);
      onFeedbackSubmit(clipId, newFeedback);
    }
  };
  
  const handleRemarksChange = (e) => {
    const newFeedback = { ...feedback, remarks: e.target.value };
    setFeedback(newFeedback);
  };
  
  const handleRemarksSubmit = () => {
    onFeedbackSubmit(clipId, feedback);
    setShowRemarks(false);
  };
  
  // Extract clip number for download filename
  const getClipNumber = () => {
    const match = clipId.match(/clip(\d+)/);
    return match ? match[1] : '';
  };
  
  return (
    <div className="clip-feedback">
      <div className="feedback-buttons">
        <button 
          className={`feedback-btn like-btn ${feedback.liked ? 'active' : ''}`}
          onClick={handleLike}
          title="Like"
        >
          <span role="img" aria-label="like">👍</span>
        </button>
        <button 
          className={`feedback-btn dislike-btn ${feedback.disliked ? 'active' : ''}`}
          onClick={handleDislike}
          title="Dislike"
        >
          <span role="img" aria-label="dislike">👎</span>
        </button>
        <button 
          className={`feedback-btn remark-btn ${showRemarks ? 'active' : ''} ${feedback.remarks ? 'has-remarks' : ''}`}
          onClick={() => setShowRemarks(!showRemarks)}
          title={feedback.remarks ? `Remarks: ${feedback.remarks}` : "Add remarks"}
        >
          <span role="img" aria-label="remarks">💬</span>
        </button>
        <button 
          className="feedback-btn download-btn"
          onClick={(e) => {
            e.stopPropagation();
            // Get the download URL directly from the clip element
            const downloadUrl = document.querySelector(`[data-clip-id="${clipId}"]`)?.getAttribute('data-download-url');
            
            if (downloadUrl) {
              // Use the parent component's handleDownload function via custom event
              const filename = `clip_${getClipNumber()}.mp4`;
              window.dispatchEvent(new CustomEvent('clip-download', {
                detail: { url: downloadUrl, filename, id: clipId }
              }));
              return;
            }
            
            // Fallback to the old method if we couldn't find the download URL
            const clips = document.querySelectorAll('.clip-thumbnail');
            for (const clip of clips) {
              if (clip.getAttribute('data-clip-id') === clipId || clip.querySelector('.debug-info')?.textContent.includes(clipId)) {
                const clipDownloadUrl = clip.getAttribute('data-download-url');
                if (clipDownloadUrl) {
                  const filename = `clip_${getClipNumber()}.mp4`;
                  window.dispatchEvent(new CustomEvent('clip-download', {
                    detail: { url: clipDownloadUrl, filename, id: clipId }
                  }));
                  return;
                }
              }
            }
            
            // If no download URL found, log an error
            console.error(`Download URL not found for clip ${clipId}`);
          }}
          title="Download clip"
        >
          <span role="img" aria-label="download">⬇️</span>
        </button>
      </div>
      
      {showRemarks && (
        <div className="remarks-container">
          <textarea
            value={feedback.remarks}
            onChange={handleRemarksChange}
            placeholder="Enter your remarks here..."
            className="remarks-input"
          />
          <button className="remarks-submit-btn" onClick={handleRemarksSubmit}>
            Save
          </button>
        </div>
      )}
      
      <div className="debug-info">
        <div>Clip ID: {clipId}</div>
        <div>Feedback state: {JSON.stringify(feedback)}</div>
      </div>
    </div>
  );
};

// TimestampClipDetails component to show clip details with start/end time
const TimestampClipDetails = ({ clip, onPlayClip, selectedTimestamp }) => {
  if (!clip || !clip.start_time) return null;
  
  return (
    <div className="timestamp-details">
      <div className="timestamp-info">
        <div className="timestamp-label">Start:</div>
        <div 
          className={`timestamp-value ${selectedTimestamp === clip.start_time ? 'active' : ''}`}
          onClick={() => onPlayClip(clip.start_time)}
        >
          {clip.start_time}
        </div>
      </div>
      <div className="timestamp-info">
        <div className="timestamp-label">End:</div>
        <div 
          className={`timestamp-value ${selectedTimestamp === clip.end_time ? 'active' : ''}`}
          onClick={() => onPlayClip(clip.end_time)}
        >
          {clip.end_time}
        </div>
      </div>
    </div>
  );
};


// Update the GroupedClipsView component
const GroupedClipsView = ({ groupedClips, onClipSelect, selectedClip, handleDownload, downloading, clipFeedback, onFeedbackSubmit }) => {
  const [variationMetadata, setVariationMetadata] = useState({});
  
  // Fetch metadata for variations when component loads
  useEffect(() => {
    const fetchVariationsMetadata = async () => {
      if (!groupedClips || Object.keys(groupedClips).length === 0) return;
      
      const metadataMap = {};
      
      for (const jobId of Object.keys(groupedClips)) {
        // Get all variations in this job
        const allClips = groupedClips[jobId];
        const variationsInJob = new Set();
        
        allClips.forEach(clip => {
          if (clip.variationNumber !== null) {
            variationsInJob.add(clip.variationNumber.toString());
          } else {
            variationsInJob.add('default');
          }
        });
        
        // Try to fetch metadata for each variation
        for (const variation of variationsInJob) {
          try {
            const videoId = jobId;
            const chunkId = variation; // Use variation as chunkId
            const key = `${jobId}-${variation}`;
            
            try {
              // Try to fetch metadata for this variation
              const metadata = await ClipMetadataService.getEnhancedClipsMetadata(
                videoId, chunkId
              );
              
              // Extract key metadata details for display
              const metaDetails = {
                clipCount: metadata.clips ? metadata.clips.length : 0,
                clips: metadata.clips,
                enhancedAt: metadata.enhanced_at,
                firstClip: metadata.clips && metadata.clips[0] ? {
                  id: metadata.clips[0].id,
                  heading: metadata.clips[0].heading,
                  viralScore: metadata.clips[0].viral_score
                } : null,
                processingInfo: metadata.processing_metadata,
                source: 'variation',
                fullMetadata: metadata,
                modelId: metadata.model_id,
                region: metadata.region,
                processingTime: metadata.processing_time,
                timestamp: metadata.timestamp
              };
              
              metadataMap[key] = metaDetails;
              console.log(`Fetched metadata for variation ${variation}:`, metaDetails);
              
            } catch (error) {
              console.error(`Error fetching metadata for variation ${variation} in job ${jobId}:`, error);
            }
          } catch (err) {
            console.error(`Error processing variation ${variation} in job ${jobId}:`, err);
          }
        }
      }
      
      setVariationMetadata(metadataMap);
    };
    
    fetchVariationsMetadata();
  }, [groupedClips]);
  
  const handleClipClick = (clip) => {
    if (onClipSelect) {
      onClipSelect(clip);
    }
  };
  
  // Add a debug utility function to print variation metadata
  const printVariationMetadata = async (jobId, variation) => {
    console.log(`Fetching metadata for job ${jobId} and variation ${variation}...`);
    try {
      // Use the ClipMetadataService to fetch metadata for this variation
      const videoId = jobId;
      const chunkId = variation; // Use variation as chunkId
      
      // First try to fetch variation-specific metadata
      try {
        const metadata = await ClipMetadataService.getEnhancedClipsMetadata(
          videoId, 
          chunkId, 
        );
        
        console.log(`Metadata for variation ${variation}:`, {
          clips: metadata.clips ? metadata.clips.length : 0,
          first_clip: metadata.clips && metadata.clips[0] ? {
            id: metadata.clips[0].id,
            heading: metadata.clips[0].heading,
            viral_score: metadata.clips[0].viral_score
          } : null,
          enhanced_at: metadata.enhanced_at,
          processing_info: metadata.processing_metadata,
          all_clips: metadata.clips
        });
        
        // Display a simple summary in the UI
        alert(`Metadata for variation ${variation}:\n` +
              `- ${metadata.clips ? metadata.clips.length : 0} clips\n` +
              `- Enhanced at: ${metadata.enhanced_at ? new Date(metadata.enhanced_at).toLocaleString() : 'unknown'}`);
              
      } catch (variationError) {
        console.error(`No specific metadata found for variation ${variation}`, variationError);
        
        // Try to fetch main metadata and check if it has variations field
        const mainMetadata = await ClipMetadataService.getEnhancedClipsMetadata(videoId, variation);
        
        if (mainMetadata.variations && mainMetadata.variations[variation]) {
          console.log(`Found variation ${variation} in main metadata:`, {
            clips: mainMetadata.variations[variation].clips ? mainMetadata.variations[variation].clips.length : 0,
            first_clip: mainMetadata.variations[variation].clips && mainMetadata.variations[variation].clips[0] ? {
              id: mainMetadata.variations[variation].clips[0].id,
              heading: mainMetadata.variations[variation].clips[0].heading,
              viral_score: mainMetadata.variations[variation].clips[0].viral_score
            } : null,
            enhanced_at: mainMetadata.variations[variation].enhanced_at,
          });
          
          // Display a simple summary in the UI
          alert(`Metadata for variation ${variation} (from main metadata):\n` +
                `- ${mainMetadata.variations[variation].clips ? mainMetadata.variations[variation].clips.length : 0} clips\n` +
                `- Enhanced at: ${mainMetadata.variations[variation].enhanced_at ? new Date(mainMetadata.variations[variation].enhanced_at).toLocaleString() : 'unknown'}`);
        } else {
          console.log(`No metadata found for variation ${variation} in main metadata`);
          alert(`No metadata found for variation ${variation}`);
        }
      }
    } catch (error) {
      console.error(`Error fetching metadata for job ${jobId} and variation ${variation}:`, error);
      alert(`Error fetching metadata for variation ${variation}: ${error.message}`);
    }
  };
  
  if (!groupedClips || Object.keys(groupedClips).length === 0) {
    return (
      <div className="no-clips-message">
        <p>No clips available for this job</p>
        <p className="sub-message">Please check back later or select a different job.</p>
      </div>
    );
  }
  
  return (
    <div className="grouped-clips-container">
      {Object.entries(groupedClips).map(([jobId, allClips]) => {
        // Group clips by variation
        const clipsByVariation = {};
        
        // First sort all clips by their variation and clip number
        allClips.forEach(clip => {
          const variation = clip.variationNumber !== null ? 
            clip.variationNumber.toString() : 'default';
          
          if (!clipsByVariation[variation]) {
            clipsByVariation[variation] = [];
          }
          
          clipsByVariation[variation].push(clip);
        });
        
        // Sort variations numerically
        const sortedVariations = Object.keys(clipsByVariation).sort((a, b) => {
          if (a === 'default') return -1;
          if (b === 'default') return 1;
          return parseInt(a, 10) - parseInt(b, 10);
        });
        
        return (
          <div key={jobId} className="job-clip-group">
            <h3 className="job-id-header">
              Job ID: {jobId}
              <span className="clip-count"> ({allClips.length} total clips)</span>
            </h3>
            
            {sortedVariations.map(variation => {
              // Sort clips within each variation by clip number
              const sortedClips = [...clipsByVariation[variation]].sort((a, b) => {
                const numA = a.clipNumber || parseInt((a.key.match(/clip(\d+)_/) || [])[1] || '0', 10);
                const numB = b.clipNumber || parseInt((b.key.match(/clip(\d+)_/) || [])[1] || '0', 10);
                return numA - numB;
              });
              
              // Get metadata for this variation if available
              const metadataKey = `${jobId}-${variation}`;
              const meta = variationMetadata[metadataKey];
              
              return (
                <div key={`${jobId}-${variation}`} className="variation-group">
                  <h4 className="variation-header">
                    {variation === 'default' ? 'Default Variation' : `Variation ${variation}`}
                    <span className="clip-count"> ({sortedClips.length} clips)</span>
                  </h4>
                  
                  {/* Display metadata details directly */}
                  {meta && (
                    <div className="variation-meta-details">
                      <div className="meta-detail">
                        <strong>Metadata:</strong> {meta.clipCount > 0 ? `${meta.clipCount} clips` : 'No clips in metadata'}
                        {meta.enhancedAt && <span className="meta-date"> • Updated: {new Date(meta.enhancedAt).toLocaleString()}</span>}
                      </div>
                      
                      {meta.modelId && (
                        <div className="meta-detail">
                          <strong>Model:</strong> {meta.modelId}
                        </div>
                      )}
                      {meta.region && (
                        <div className="meta-detail">
                          <strong>Region:</strong> {meta.region}
                        </div>
                      )}
                      {meta.processingTime && (
                        <div className="meta-detail">
                          <strong>Processing Time:</strong> {meta.processingTime} seconds
                        </div>
                      )}
                      {meta.timestamp && (
                        <div className="meta-detail">
                          <strong>Timestamp:</strong> {meta.timestamp}
                        </div>
                      )}

                      
                      {meta.processingInfo && (
                        <div className="meta-detail">
                          <strong>Processing:</strong> {meta.processingInfo.processed_at && 
                            new Date(meta.processingInfo.processed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="clips-row">
                    {sortedClips.map((clip, index) => {
                      // Get clip number from the processed property or filename
                      const clipNumber = clip.clipNumber || (
                        clip.key && clip.key.match(/clip(\d+)_/) ? 
                        clip.key.match(/clip(\d+)_/)[1] : 
                        index + 1
                      );
                      
                      return (
                        <div 
                          key={clip.uniqueId || clip.key || index}
                          className={`clip-thumbnail ${selectedClip && selectedClip.key === clip.key ? 'selected' : ''}`}
                          onClick={() => handleClipClick(clip)}
                          data-clip-id={clip.key}
                          data-download-url={clip.downloadUrl || ''}
                          style={{ transform: 'none', transition: 'none' }}
                        >
                          {clip.downloadUrl ? (
                            <div className="video-preview reel-format">
                              <div className="reel-video-container">
                                <div className="reel-video-wrapper">
                                  <VideoPlayer 
                                    src={clip.downloadUrl} 
                                    fallbackSrc={clip.fallbackUrl}
                                    onLoad={() => {}}
                                    isReel={true}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="clip-placeholder">
                              <span className="clip-icon">🎬</span>
                            </div>
                          )}
                          
                          {/* Add clip timestamp info */}
                          {meta && meta.clips && Array.isArray(meta.clips) && 
                           index < meta.clips.length && meta.clips[index] && 
                           meta.clips[index].start_time && meta.clips[index].end_time && (
                            <div className="clip-details">
                              {/* Clip summary line with number, viral score and duration */}
                              <div className="clip-summary-row">
                                <span className="clip-number">CLIP {clipNumber}</span>
                                {meta.clips[index].viral_score && (
                                  <span className="clip-summary-score">{meta.clips[index].viral_score}/10</span>
                                )}
                                {meta.clips[index].duration && (
                                  <span className="clip-summary-duration">
                                    {`${Math.floor(meta.clips[index].duration / 60)}:${
                                      String(Math.floor(meta.clips[index].duration % 60)).padStart(2, '0')
                                    }`}
                                  </span>
                                )}
                              </div>
                              {/* Add ClipFeedback component */}
                              {onFeedbackSubmit && (
                                <ClipFeedback 
                                  clipId={clip.key}
                                  initialFeedback={clipFeedback[clip.key]} 
                                  onFeedbackSubmit={onFeedbackSubmit}
                                />
                              )}
                              {/* Start time indicator with green left border */}
                              <div 
                                className="time-row start-time"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.dispatchEvent(new CustomEvent('play-at-timestamp', {
                                    detail: { timestamp: meta.clips[index].start_time }
                                  }));
                                }}
                                title={`Start: ${meta.clips[index].start_time}`}
                              >
                                <span className="time-label">START:</span>
                                <span className="time-value">{meta.clips[index].start_time}</span>
                              </div>
                              
                              {/* End time indicator with red left border */}
                              <div 
                                className="time-row end-time"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.dispatchEvent(new CustomEvent('play-at-timestamp', {
                                    detail: { timestamp: meta.clips[index].end_time }
                                  }));
                                }}
                                title={`End: ${meta.clips[index].end_time}`}
                              >
                                <span className="time-label">END:</span>
                                <span className="time-value">{meta.clips[index].end_time}</span>
                              </div>
                              
                              {/* Format and display duration in min:sec format */}
                              {/* {meta && meta.clips && Array.isArray(meta.clips) && 
                               index < meta.clips.length && meta.clips[index] && meta.clips[index].duration && (
                                <div className="duration-row">
                                  <span className="duration-label">DURATION:</span>
                                  <span className="duration-value">
                                    {`${Math.floor(meta.clips[index].duration / 60)}:${
                                      String(Math.floor(meta.clips[index].duration % 60)).padStart(2, '0')
                                    }`}
                                  </span>
                                </div>
                              )} */}
                              
                              {/* Viral score section */}
                              {/* {meta && meta.clips && Array.isArray(meta.clips) && 
                               index < meta.clips.length && meta.clips[index] && meta.clips[index].viral_score && (
                                <div className="score-row">
                                  <span className="score-label">VIRAL SCORE:</span>
                                  <span className="score-badge">{meta.clips[index].viral_score}/10</span>
                                </div>
                              )} */}
                              
                              {/* Heading as colored bar */}
                              {meta && meta.clips && Array.isArray(meta.clips) && 
                               index < meta.clips.length && meta.clips[index] && meta.clips[index].heading && (
                                <div className="clip-heading-bar">
                                  {meta.clips[index].heading}
                                </div>
                              )}
                              
                              {/* Caption as subtle text */}
                              {meta && meta.clips && Array.isArray(meta.clips) && 
                               index < meta.clips.length && meta.clips[index] && meta.clips[index].small_caption && (
                                <div className="clip-caption-text">
                                  {meta.clips[index].small_caption}
                                </div>
                              )}
                              
                              {/* Content container with expand/collapse */}
                              <div className="clip-content-container collapsed">
                                {/* Toggle expander for all content */}
                                <div className="content-expander">
                                  <button 
                                    className="toggle-content-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Toggle the expanded class on the parent container
                                      const container = e.currentTarget.closest('.clip-content-container');
                                      container.classList.toggle('collapsed');
                                      
                                      // Update the toggle text based on the collapsed state
                                      const toggleText = e.currentTarget.querySelector('.toggle-text');
                                      const toggleIcon = e.currentTarget.querySelector('.toggle-icon');
                                      if (container.classList.contains('collapsed')) {
                                        toggleText.textContent = 'Show More';
                                        toggleIcon.textContent = '▼';
                                      } else {
                                        toggleText.textContent = 'Show Less';
                                        toggleIcon.textContent = '▲';
                                      }
                                    }}
                                  >
                                    <span className="toggle-icon">▼</span>
                                    <span className="toggle-text">Show More</span>
                                  </button>
                                </div>
                                
                                {/* Viral factors section with toggle */}
                                {meta && meta.clips && Array.isArray(meta.clips) && 
                                 index < meta.clips.length && meta.clips[index] && 
                                 meta.clips[index].viral_factors && Array.isArray(meta.clips[index].viral_factors) && 
                                 meta.clips[index].viral_factors.length > 0 && (
                                  <div className="factors-section expandable-section collapsed">
                                    <div className="section-header" 
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           const section = e.currentTarget.closest('.expandable-section');
                                           section.classList.toggle('collapsed');
                                           // Update toggle icon/text
                                           const toggle = e.currentTarget.querySelector('.section-toggle');
                                           toggle.textContent = section.classList.contains('collapsed') ? '▼' : '▲';
                                         }}>
                                      <span>VIRAL FACTORS:</span>
                                      <span className="section-toggle">▼</span>
                                    </div>
                                    <div className="factors-list section-content">
                                      {meta.clips[index].viral_factors.map((factor, i) => (
                                        <div key={i} className="factor-item">
                                          {factor}
                                          <button 
                                            className="copy-item-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard.writeText(factor)
                                                .then(() => showToast(`Copied: ${factor}`))
                                                .catch(err => console.error('Failed to copy: ', err));
                                            }}
                                            title="Copy this factor"
                                          >
                                            <span className="copy-icon">📋</span>
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Hashtags section with copy button */}
                                {meta && meta.clips && Array.isArray(meta.clips) && 
                                 index < meta.clips.length && meta.clips[index] && 
                                 meta.clips[index].hashtag_categories && Array.isArray(meta.clips[index].hashtag_categories) && 
                                 meta.clips[index].hashtag_categories.length > 0 && (
                                  <div className="hashtags-section expandable-section collapsed">
                                    <div className="section-header"
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           const section = e.currentTarget.closest('.expandable-section');
                                           section.classList.toggle('collapsed');
                                           // Update toggle icon/text
                                           const toggle = e.currentTarget.querySelector('.section-toggle');
                                           toggle.textContent = section.classList.contains('collapsed') ? '▼' : '▲';
                                         }}>
                                      <span>HASHTAGS:</span>
                                      <div className="header-actions">
                                        <button 
                                          className="copy-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const hashtags = meta.clips[index].hashtag_categories
                                              .map(tag => `${tag.replace(/\s+/g, '')}`).join(' ');
                                            navigator.clipboard.writeText(hashtags)
                                              .then(() => showToast('Hashtags copied to clipboard!'))
                                              .catch(err => console.error('Failed to copy: ', err));
                                          }}
                                          title="Copy all hashtags"
                                        >
                                          Copy All
                                        </button>
                                        <span className="section-toggle">▼</span>
                                      </div>
                                    </div>
                                    <div className="hashtags-list section-content">
                                      {meta.clips[index].hashtag_categories.map((tag, i) => (
                                        <span key={i} className="hashtag-pill">{tag.replace(/\s+/g, '')}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Engagement prompts section */}
                                {meta && meta.clips && Array.isArray(meta.clips) && 
                                 index < meta.clips.length && meta.clips[index] && 
                                 meta.clips[index].audience_engagement_prompts && 
                                 Array.isArray(meta.clips[index].audience_engagement_prompts) && 
                                 meta.clips[index].audience_engagement_prompts.length > 0 && (
                                  <div className="prompts-section expandable-section collapsed">
                                    <div className="section-header"
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           const section = e.currentTarget.closest('.expandable-section');
                                           section.classList.toggle('collapsed');
                                           // Update toggle icon/text
                                           const toggle = e.currentTarget.querySelector('.section-toggle');
                                           toggle.textContent = section.classList.contains('collapsed') ? '▼' : '▲';
                                         }}>
                                      <span>ENGAGEMENT PROMPTS:</span>
                                      <span className="section-toggle">▼</span>
                                    </div>
                                    <div className="prompt-container section-content">
                                      {meta.clips[index].audience_engagement_prompts.map((prompt, i) => (
                                        <div key={i} className="prompt-quote">
                                          {prompt}
                                          <button 
                                            className="copy-item-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard.writeText(prompt)
                                                .then(() => showToast(`Copied: ${prompt.substring(0, 30)}...`))
                                                .catch(err => console.error('Failed to copy: ', err));
                                            }}
                                            title="Copy this prompt"
                                          >
                                            <span className="copy-icon">📋</span>
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Hook suggestions if available */}
                                {meta && meta.clips && Array.isArray(meta.clips) && 
                                 index < meta.clips.length && meta.clips[index] && 
                                 meta.clips[index].hook_suggestions && 
                                 Array.isArray(meta.clips[index].hook_suggestions) && 
                                 meta.clips[index].hook_suggestions.length > 0 && (
                                  <div className="hooks-section expandable-section collapsed">
                                    <div className="section-header"
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           const section = e.currentTarget.closest('.expandable-section');
                                           section.classList.toggle('collapsed');
                                           // Update toggle icon/text
                                           const toggle = e.currentTarget.querySelector('.section-toggle');
                                           toggle.textContent = section.classList.contains('collapsed') ? '▼' : '▲';
                                         }}>
                                      <span>HOOK SUGGESTIONS:</span>
                                      <span className="section-toggle">▼</span>
                                    </div>
                                    <div className="hooks-container section-content">
                                      {meta.clips[index].hook_suggestions.map((hook, i) => (
                                        <div key={i} className="hook-item">
                                          {hook}
                                          <button 
                                            className="copy-item-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigator.clipboard.writeText(hook)
                                                .then(() => showToast(`Copied: ${hook.substring(0, 30)}...`))
                                                .catch(err => console.error('Failed to copy: ', err));
                                            }}
                                            title="Copy this hook"
                                          >
                                            <span className="copy-icon">📋</span>
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const PAGE_SIZE = 20;

  const refreshTimerRef = useRef(null);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    fetchJobs(true, 1, '');

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      setJobs([]);
      setLoading(false);
      setError(null);
    };
  }, []);

  // Debounce search input — waits 400ms after the user stops typing, then triggers a server search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Reset to page 1 on new search
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch jobs when debounced search or page changes
  useEffect(() => {
    fetchJobs(true, currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    // Handle auto-refresh setup/cleanup
    if (autoRefresh) {
      refreshTimerRef.current = setInterval(() => {
        fetchJobs(false); // Don't show loading indicator for auto-refresh
      }, refreshInterval * 1000);
    } else if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    // Clean up on effect cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshInterval]);


  const fetchJobs = async (showLoading = true, page = currentPage, search = debouncedSearch) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const data = await DashboardService.getAllJobs({ page, limit: PAGE_SIZE, search });

      // Update pagination state from server response
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages || 1);
        setTotalJobs(data.pagination.total || 0);
        setCurrentPage(data.pagination.page || page);
      }

      // Group jobs by Video ID / job ID
      const groupedJobs = {};
      (data.jobs || []).forEach(job => {
        // Ensure we get the full job ID
        const jobId = job.videoId || getVideoId(job.object_key);

        // Skip if we can't extract a job ID
        if (!jobId) {
          console.warn('Could not extract job ID from', job);
          return;
        }

        // Skip metadata entries (video IDs ending with "metadata")
        if (jobId.endsWith('metadata')) {
          console.log('Skipping metadata entry:', jobId);
          return;
        }

        if (!groupedJobs[jobId]) {
          groupedJobs[jobId] = {
            ...job,
            // Track all IDs from the original jobs that share this job ID
            originalIds: [job.id],
            // Store the original jobs array for reference
            relatedJobs: [job]
          };
        } else {
          // Add this job's ID to the list
          groupedJobs[jobId].originalIds.push(job.id);
          groupedJobs[jobId].relatedJobs.push(job);

          // Update lastUpdated if this job is newer
          if (new Date(job.processed_at || job.created_at) >
              new Date(groupedJobs[jobId].processed_at || groupedJobs[jobId].created_at)) {
            groupedJobs[jobId].processed_at = job.processed_at;
          }
        }
      });

      // Convert the grouped jobs object to an array
      const groupedJobsArray = Object.values(groupedJobs);

      // Sort by most recent first
      groupedJobsArray.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setJobs(groupedJobsArray);
      setError(null);
    } catch (err) {
      setError('Failed to load jobs. Please try again.');
      console.error(err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };

  const getVideoId = (objectKey) => {
    // Check if the object key is a valid UUID format (with potential .json extension)
    const uuidMatch = objectKey.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(?:\.json)?$/);
    if (uuidMatch) {
      return uuidMatch[1];
    }
    
    // Fallback to old behavior for backward compatibility
    return objectKey.split('/').pop().replace('.json', '');
  };

  const handleAutoRefreshToggle = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleRefreshIntervalChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setRefreshInterval(value);
    }
  };

  // Add key handler for debug mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey) {
        document.body.classList.add('debug-mode');
        console.log('Debug mode enabled - debug info is now visible');
      }
    };
    
    const handleKeyUp = (e) => {
      if (!e.altKey) {
        document.body.classList.remove('debug-mode');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Page navigation helpers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="dashboard-container">
      <h1>Processing Dashboard</h1>
      
      <div className="dashboard-content">
        {loading ? (
          <Loader />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="jobs-list">
              <div className="jobs-header">
                <h2>Processing Jobs</h2>
                <div className="header-actions">
                  <div className="auto-refresh-controls">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={autoRefresh} 
                        onChange={handleAutoRefreshToggle}
                      /> 
                      Auto-refresh
                    </label>
                    {autoRefresh && (
                      <div className="refresh-interval">
                        <input 
                          type="number" 
                          min="5" 
                          value={refreshInterval} 
                          onChange={handleRefreshIntervalChange}
                        />
                        <span>seconds</span>
                      </div>
                    )}
                  </div>
                  <button className="refresh-btn" onClick={() => fetchJobs()}>
                    Refresh
                  </button>
                </div>
              </div>

              <div className="search-controls">
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Search jobs (file name, user, video ID, status...)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="clear-search-btn"
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="job-count-info">
                  {debouncedSearch.trim()
                    ? `${totalJobs} results for "${debouncedSearch}"`
                    : `${totalJobs} total jobs`}
                  {totalPages > 1 && ` — Page ${currentPage} of ${totalPages}`}
                </div>
              </div>

              <div className="jobs-table-container">
                <div className="table-wrapper">
                  <table className="modern-jobs-table">
                    <thead>
                      <tr>
                        <th className="col-video-id">Video ID</th>
                        <th className="col-file-name">File Name</th>
                        <th className="col-user">User</th>
                        <th className="col-status">Status</th>
                        <th className="col-created">Created</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="no-jobs">
                            <div className="empty-state">
                              <div className="empty-title">{debouncedSearch ? 'No results found' : 'No jobs found'}</div>
                              <div className="empty-subtitle">
                                {debouncedSearch
                                  ? `No jobs match your search "${debouncedSearch}"`
                                  : 'Processing jobs will appear here'}
                              </div>
                              {debouncedSearch && (
                                <button
                                  onClick={() => setSearchTerm('')}
                                  className="clear-search-link"
                                >
                                  Clear search
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          return jobs.map((job) => {
                            const jobId = job.videoId || getVideoId(job.object_key);
                            return (
                              <React.Fragment key={job.id}>
                                <tr className="job-row">
                                  <td className="col-video-id">
                                    <div className="video-id-cell">
                                      <span className="video-id-text" title={jobId}>
                                        {jobId.length > 20 ? `${jobId.substring(0, 17)}...` : jobId}
                                      </span>
                                      <button 
                                        className="copy-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigator.clipboard.writeText(jobId);
                                          showToast('Video ID copied!');
                                        }}
                                        title="Copy Video ID"
                                      >
                                        Copy
                                      </button>
                                    </div>
                                  </td>
                                  <td className="col-file-name">
                                    {job.file?.originalFilename ? (
                                      <div className="file-info">
                                        <div className="file-details">
                                          <div className="filename" title={job.file.originalFilename}>
                                            {job.file.originalFilename.length > 30 
                                              ? `${job.file.originalFilename.substring(0, 27)}...` 
                                              : job.file.originalFilename}
                                          </div>
                                          {job.file.fileSource && (
                                            <div className="file-source">
                                              {job.file.fileSource === 'gdrive' ? 'Google Drive' : 
                                               job.file.fileSource === 'upload' ? 'Direct Upload' : 
                                               job.file.fileSource}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="no-data">No file info</span>
                                    )}
                                  </td>
                                  <td className="col-user">
                                    {job.user ? (
                                      <div className="user-cell">
                                        {job.user.photoUrl && (
                                          <img 
                                            src={job.user.photoUrl} 
                                            alt="User" 
                                            className="user-avatar"
                                          />
                                        )}
                                        <div className="user-details">
                                          <div className="user-name" title={job.user.displayName || job.user.email}>
                                            {job.user.displayName || job.user.email?.split('@')[0] || 'Unknown'}
                                          </div>
                                          {job.user.email && (
                                            <div className="user-email" title={job.user.email}>
                                              {job.user.email.length > 20 
                                                ? `${job.user.email.substring(0, 17)}...` 
                                                : job.user.email}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="no-data">Anonymous</span>
                                    )}
                                  </td>
                                  <td className="col-status">
                                    <span 
                                      className={`status-indicator-only ${getStatusClass(job.status)}`}
                                      title={job.status}
                                    >
                                    </span>
                                  </td>
                                  <td className="col-created">
                                    <div className="date-cell">
                                      <div className="date-primary">
                                        {new Date(job.created_at).toLocaleDateString()}
                                      </div>
                                      <div className="date-secondary">
                                        {new Date(job.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="col-actions">
                                    <button 
                                      className="open-app-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(`https://clips.polarcut.io/results?fileId=${jobId}`, '_blank');
                                      }}
                                      title="Open in App"
                                    >
                                      Open App
                                    </button>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          });
                        })()
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {(() => {
                      const pages = [];
                      let start = Math.max(1, currentPage - 2);
                      let end = Math.min(totalPages, currentPage + 2);
                      if (currentPage <= 3) end = Math.min(totalPages, 5);
                      if (currentPage >= totalPages - 2) start = Math.max(1, totalPages - 4);

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <button
                            key={i}
                            className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
                            onClick={() => goToPage(i)}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}

                    <button
                      className="pagination-btn"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

// Add inline styles for the new buttons
const styles = `
  .header-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .load-all-btn {
    background: #007bff;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
  }
  
  .load-all-btn:hover {
    background: #0056b3;
  }
  
  .all-jobs-status {
    color: #28a745;
    font-size: 14px;
    font-weight: 500;
  }
  
  .clips-status {
    color: #6c757d;
    font-size: 16px;
    font-weight: normal;
  }

  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    padding: 16px 0;
  }

  .pagination-btn {
    background: #1a1a2e;
    color: #ccc;
    border: 1px solid #333;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    transition: background-color 0.2s, color 0.2s;
  }

  .pagination-btn:hover:not(:disabled) {
    background: #007bff;
    color: #fff;
    border-color: #007bff;
  }

  .pagination-btn.active {
    background: #007bff;
    color: #fff;
    border-color: #007bff;
    font-weight: 600;
  }

  .pagination-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Inject styles into the page
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
} 